import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import format from "date-fns/format";
import { BillingModelEnums, OnlineVisit } from "src/api/types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { useCallback, useEffect, useMemo, useState } from "react";
import Answers from "../Answers";
import MagicLinkButton from "src/shared/MagicLinkButton";
import { useUpdatePatientVisitMutation } from "src/api/services/onlineVisitsApi";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import isBefore from "date-fns/isBefore";
import subDays from "date-fns/subDays";
import AlertFlag from "src/components/AlertFlag";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useIntakeSummaryQuery } from "src/api/services/intakeSummarizerApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { theme } from "src/assets/theme";
import Summary from "../Summary";

type DetailsView = "answers" | "summary-loading" | "summary" | "summary-error";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(180deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const billingModelLabels: Record<BillingModelEnums, string> = {
  one_time: "One Time",
  subscription: "Membership",
};

interface Props {
  intake: OnlineVisit;
  expanded: string | null;
  onExpand: (newExpanded: string | null) => void;
  onUpdate: (visit: OnlineVisit | null) => void;
  patientEmail: string;
  patientStatus: string | null;
  blowUp: boolean;
  setBlowUp: (v: boolean) => void;
}

const PatientIntake = ({
  intake,
  expanded,
  onExpand,
  patientEmail,
  onUpdate,
  patientStatus,
  setBlowUp,
}: Props) => {
  const [updateVisit, { isLoading }] = useUpdatePatientVisitMutation();
  const clinicianId = useClinician((c) => c.id);
  const [slug, setSlug] = useState<string | null>(null);
  const {
    data,
    isFetching: fetchingSummary,
    isError: summaryError,
    refetch,
  } = useIntakeSummaryQuery(slug ?? skipToken);
  const [requestSummary, setRequestSummary] = useState<boolean>(false);
  const [detailsView, setDetailsView] =
    useState<DetailsView>("summary-loading");
  const selectedPrice = useMemo(() => {
    const price = (intake.metadata as Record<string, string>)?.price;

    return price ?? null;
  }, [intake.metadata]);

  const handleComplete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      updateVisit({
        id: intake.id,
        patient_id: intake.patient_id,
        body: {
          status:
            intake.visit_type === "Initial" ? "completed" : "auto_completed",
          completed_at: new Date().toISOString(),
        },
      });
    },
    [intake.id, intake.patient_id, intake.visit_type, updateVisit],
  );

  const handleUpdate = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onUpdate(intake);
    },
    [intake, onUpdate],
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newExpanded: boolean) => {
      onExpand(newExpanded ? intake.slug : null);
    },
    [intake.slug, onExpand],
  );

  const handleSummarize = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setRequestSummary(true);
      setSlug(intake.slug);
      setBlowUp(true);
    },
    [intake.slug, setBlowUp],
  );

  const handleRetrySummary = useCallback(() => {
    if (slug != null) {
      refetch();
    }
  }, [refetch, slug]);

  const showComplete = useMemo(() => {
    if (intake.status !== "created") return false;
    if (intake.visit_type === "Check-in") return true;
    if (intake.visit_type === "Initial") return true;
    if (intake.visit_type === "Upsell") return true;
    if (intake.visit_type === "Refill" && intake.due_at != null) {
      const sixDaysBefore = subDays(new Date(intake.due_at), 6);
      return isBefore(sixDaysBefore, new Date());
    }
  }, [intake.status, intake.visit_type, intake.due_at]);

  const showUpdate = useMemo(() => {
    if (!["Lead", "Registered"].includes(patientStatus ?? "")) return false;
    if (intake.status !== "created") return false;

    return true;
  }, [intake.status, patientStatus]);

  const handleConsentCheck = async (isChecked: boolean, visitId: number) => {
    await updateVisit({
      id: visitId,
      patient_id: intake.patient_id,
      body: {
        patient_consent_to_complete: isChecked,
        consent_checked_by: isChecked ? clinicianId : null,
      },
    });
  };

  const displayConsentCheckbox = useMemo(
    () => intake.visit_type === "Initial" && intake.completed_at === null,
    [intake.completed_at, intake.visit_type],
  );

  const visitType = useMemo(() => {
    if (intake.visit_type === "Upsell") {
      return null;
    }

    return intake.visit_type;
  }, [intake.visit_type]);

  const medicationForm = useMemo(() => {
    const form = intake.metadata?.medication_form;

    if (form == null) return null;

    return `(${form})`;
  }, [intake.metadata]);

  const flag = useMemo(() => {
    const flagColor = intake.metadata?.flag;

    if (flagColor == null) return null;

    return flagColor;
  }, [intake.metadata?.flag]);

  const intakeName = useMemo(() => {
    return intake.product.name.replace(/\bOT\b/g, "");
  }, [intake.product.name]);

  const billingModel = useMemo(
    () => billingModelLabels[intake.product.billing_model],
    [intake.product.billing_model],
  );

  const intakeInformation = useMemo(() => {
    switch (intake.status) {
      case "created": {
        return `Started on ${format(
          new Date(intake.created_at),
          "MM/dd/yyyy",
        )}`;
      }
      case "completed": {
        return `Completed on ${format(
          new Date(intake.completed_at || ""),
          "MM/dd/yyyy",
        )}`;
      }
      case "canceled": {
        return `Canceled on ${format(
          new Date(intake.canceled_at || ""),
          "MM/dd/yyyy",
        )}`;
      }
      case "auto-completed": {
        return `Auto-completed on ${format(
          new Date(intake.completed_at || ""),
          "MM/dd/yyyy",
        )}`;
      }
      default: {
        return intake.status;
      }
    }
  }, [
    intake.canceled_at,
    intake.completed_at,
    intake.created_at,
    intake.status,
  ]);

  useEffect(() => {
    if (!requestSummary) {
      setDetailsView("answers");
      return;
    }

    if (summaryError) {
      setDetailsView("summary-error");
      return;
    }
    if (data != null) {
      setDetailsView("summary");
      return;
    }

    setDetailsView("summary-loading");
  }, [data, requestSummary, summaryError]);

  return (
    <Stack width={"100%"}>
      <Accordion expanded={expanded === intake.slug} onChange={handleChange}>
        <AccordionSummary
          component="div"
          expandIcon={<ArrowDropDownIcon />}
          aria-controls={`panel${intake.id}-content`}
          id={`panel${intake.id}-header`}
        >
          <Stack>
            <Stack direction="row" gap={1} alignItems="center">
              {intake.status === "completed" &&
              intake.product_category.name === "Weight Loss" &&
              expanded != null ? (
                <Tooltip title="Summarize intake">
                  <IconButton onClick={handleSummarize} size="small">
                    {fetchingSummary ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AutoAwesomeIcon />
                    )}
                  </IconButton>
                </Tooltip>
              ) : null}
              <Typography fontWeight={700} variant="body2">
                {intakeName} {billingModel} {visitType} {medicationForm}
              </Typography>
              {flag ? (
                <AlertFlag
                  color={flag}
                  text={
                    flag === "success"
                      ? "Based on their intake this patient is eligible for GLP-1 medications"
                      : "Patient indicated a history of pancreatitis, please review"
                  }
                />
              ) : null}
              {showComplete ? (
                <Button
                  loading={isLoading}
                  size="small"
                  onClick={handleComplete}
                  variant="outlined"
                  sx={{ p: 0, pl: 1, pr: 1 }}
                >
                  Complete
                </Button>
              ) : null}
              {showUpdate ? (
                <Button
                  onClick={handleUpdate}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  sx={{ p: 0, pl: 1, pr: 1 }}
                >
                  Updates
                </Button>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1" fontStyle="italic">
                {intakeInformation}
              </Typography>
            </Stack>
            {intake.completed_at &&
            intake.patient_consent_to_complete &&
            intake.consent_checked_by ? (
              <Typography variant="subtitle1">{`Consent to complete checked by: ${intake.profile.first_name} ${intake.profile.last_name}`}</Typography>
            ) : null}
          </Stack>
        </AccordionSummary>
        {detailsView === "summary-error" ? (
          <AccordionDetails>
            <Stack spacing={1} py={1}>
              <Typography variant="body2" color="error">
                The summary couldn't be generated. You can retry, or view the
                raw intake answers below.
              </Typography>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRetrySummary}
                >
                  Retry summary
                </Button>
              </Box>
              <Answers intakeSlug={intake.slug} />
            </Stack>
          </AccordionDetails>
        ) : detailsView === "summary" ? (
          <AccordionDetails>
            <Box
              p={1.5}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                background: theme.palette.teal[200],
                boxShadow: 5,
              }}
            >
              <Summary summary={data} />
              <Button
                onClick={() => setDetailsView("answers")}
                sx={{ marginTop: 2 }}
                variant="alternate"
              >
                Display answers
              </Button>
            </Box>
          </AccordionDetails>
        ) : (
          <AccordionDetails>
            <Stack gap={2}>
              <Answers
                intakeSlug={expanded === intake.slug ? intake.slug : null}
                patientId={intake.patient_id}
              />
              {selectedPrice != null ? (
                <Typography>
                  Price:{" "}
                  <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                    {selectedPrice}
                  </span>
                </Typography>
              ) : null}
              {data != null ? (
                <Button
                  onClick={() => setDetailsView("summary")}
                  sx={{ width: "fit-content" }}
                >
                  Display summary
                </Button>
              ) : null}
            </Stack>
          </AccordionDetails>
        )}
      </Accordion>

      <Stack pl={2} gap={1} width="300px" alignItems="end">
        {displayConsentCheckbox ? (
          <FormControlLabel
            checked={intake.patient_consent_to_complete}
            control={
              <Checkbox
                onChange={(e) =>
                  handleConsentCheck(e.target.checked, intake.id)
                }
              />
            }
            label="Patient gave consent to complete online visit on their behalf"
          />
        ) : null}

        {intake.patient_consent_to_complete && !intake.completed_at ? (
          <MagicLinkButton
            patientEmail={patientEmail}
            buttonText="Complete intake"
          />
        ) : null}
      </Stack>
    </Stack>
  );
};

export default PatientIntake;
